import {
  LocaleEnum,
  MarketPlace,
  Placement,
  DeviceFamily,
} from '@amzn/fire-tv-campaign-automation-service';
import { createContext, Dispatch, SetStateAction, useState, useMemo, useContext } from 'react';
import { getPlacementsForDeviceFamily } from 'src/config/deviceFamilyPlacementConfig';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';
import { ProvisionedConstants, STAGE } from 'src/constants';
import { usePersistState } from 'src/hooks/usePersistState';

interface SelectionType {
  marketplace: MarketPlace;
  placement: Placement;
  locale: LocaleEnum;
  deviceFamily: DeviceFamily;
  setMarketplace: Dispatch<SetStateAction<MarketPlace>>;
  setPlacement: Dispatch<SetStateAction<Placement>>;
  setLocale: Dispatch<SetStateAction<LocaleEnum | null>>;
  setDeviceFamily: Dispatch<SetStateAction<DeviceFamily>>;
  updateLocaleForMarketplace: (marketplace: MarketPlace) => void;
}

const SelectionContext = createContext<SelectionType>({
  marketplace: CMSConfig?.MARKET_PLACE[0],
  placement: Placement.FEATURE_ROTATOR,
  locale: CMSConfig?.LOCALE[CMSConfig?.MARKET_PLACE[0]]?.[0] || LocaleEnum.EN_US,
  deviceFamily: DeviceFamily.FIRE_TV,
  setMarketplace: () => {},
  setPlacement: () => {},
  setLocale: () => {},
  setDeviceFamily: () => {},
  updateLocaleForMarketplace: () => {},
});

/**
 * Hook to provide selection state management with circular dependency prevention
 */
const useSelectionState = () => {
  const [marketplace, setMarketplace] = usePersistState<MarketPlace>(
    'selectedMarketplace',
    CMSConfig?.MARKET_PLACE?.[0],
    { storage: 'localStorage' },
  );
  const [placement, setPlacement] = usePersistState<Placement>(
    'selectedPlacement',
    Placement.FEATURE_ROTATOR,
    { storage: 'localStorage' },
  );
  const [deviceFamily, setDeviceFamily] = usePersistState<DeviceFamily>(
    'selectedDeviceFamily',
    DeviceFamily.FIRE_TV,
    { storage: 'localStorage' },
  );

  // Validate persisted placement against current deviceFamily
  const validPlacements = getPlacementsForDeviceFamily(
    deviceFamily,
    ProvisionedConstants.STAGE as STAGE,
  );
  if (!validPlacements.includes(placement)) {
    setPlacement(validPlacements[0] ?? Placement.FEATURE_ROTATOR);
  }

  // Derive default locale from marketplace
  const defaultLocale = useMemo(() => {
    const marketplaceLocales = CMSConfig?.LOCALE?.[marketplace];
    const fallbackLocales = CMSConfig?.LOCALE[CMSConfig?.MARKET_PLACE[0]];
    return marketplaceLocales?.[0] || fallbackLocales?.[0] || LocaleEnum.EN_US;
  }, [marketplace]);

  // Override state for user-selected locale
  const [localeOverride, setLocaleOverride] = useState<LocaleEnum | null>(null);

  return useMemo(
    () => ({
      marketplace,
      placement,
      deviceFamily,
      locale: localeOverride || defaultLocale,
      setMarketplace,
      setPlacement,
      setDeviceFamily,
      setLocale: setLocaleOverride,
      updateLocaleForMarketplace: (newMarketplace: MarketPlace) => {
        const newDefaultLocale = CMSConfig?.LOCALE?.[newMarketplace]?.[0];
        if (newDefaultLocale && !localeOverride) {
          // Only auto-update if no override is set
          setLocaleOverride(null);
        }
      },
    }),
    [marketplace, placement, deviceFamily, localeOverride, defaultLocale],
  );
};

const useSelection = (): SelectionType => {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionContextProvider');
  }
  return context;
};

export { SelectionContext, useSelection, useSelectionState };
